import { AuthServices } from '@/features/auth/services';
import { FULL_PINS } from '@/features/player-session/services';
import { buildPinUrl } from '@/features/player-session/utils';
import { LAST_ACTIVE_OPTIONS } from '@/features/player/utils/helpers';
import FastImage from '@d11/react-native-fast-image';

export const digitsOnly = (value: string) => value.replace(/\D/g, '');

export function countPhoneDigits(value: string | undefined): number {
  if (!value) return 0;
  return (value.match(/\d/g) ?? []).length;
}

export const shouldUploadAvatarUri = (uri?: string | null): uri is string => {
  if (!uri) return false;
  const trimmed = uri.trim();
  if (!trimmed) return false;
  // Only skip remote URIs (prefilled from storage URL). Any non-http(s) URI is treated as local.
  // iOS image pickers may return `ph://` or `assets-library://`.
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return false;
  return true;
};

export const getInactiveKeyFromNumber = (number: number) => {
  const option = LAST_ACTIVE_OPTIONS.find(_option => _option.seconds === number);
  if (!option) return 'show_all';
  return option.text;
};

export const prefetchImageLevelIcon = () => {
  const uriPins = FULL_PINS.map(pin => buildPinUrl(pin.pinPicture)).filter(uri => uri !== null);
  FastImage.preload(uriPins.map(uri => ({ uri, cache: 'immutable' })));
};

/**
 * Web `launchImageLibrary` often returns `data:...;base64,...` with no `fileSize`.
 * Derive decoded byte length from the base64 payload without decoding the whole string.
 */
export function byteLengthFromDataUri(uri: string): number | undefined {
  const comma = uri.indexOf(',');
  if (comma === -1) return undefined;
  const meta = uri.slice(0, comma);
  const payload = uri.slice(comma + 1);
  if (!payload) return undefined;

  if (/;base64/i.test(meta)) {
    const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
    return Math.floor((payload.length * 3) / 4) - padding;
  }

  try {
    return new TextEncoder().encode(decodeURIComponent(payload)).length;
  } catch {
    return undefined;
  }
}

/**
 * Runs `verifyAccount()` before the wrapped work.
 *
 * Prefer passing a THUNK (`() => Promise`) when the work has a side-effect
 * (e.g. a biometric prompt): a bare `Promise` argument is evaluated eagerly by
 * JS *before* this function runs, so its side-effect fires regardless of the
 * verify result. A thunk is only invoked after `verifyAccount()` succeeds.
 */
export const withVerifyAccount = async <T>(input: Promise<T> | (() => Promise<T>)) => {
  await AuthServices.verifyAccount();
  return await (typeof input === 'function' ? input() : input);
};
